// Dashboard.tsx
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from '../../app/store';
import {useLazyGetApiDashboardLayoutQuery, useLazyGetApiDashboardWidgetsQuery} from '../../api/services/Dashboard';
import WidgetRenderer from "../../helper/WidgetRenderer";
import {GetHighestAuthOrder} from "../../hooks/Authorization/AuthorizationIds";

interface WidgetPlacement {
    row: number;
    col: number;
    widgetId: string;
}

interface Widget {
    id: string;
    name: string;
    componentKey: string;
    allowedRoles: string[];
}

interface LayoutResponse {
    grid: WidgetPlacement[];
    layoutKey: string;
}

const layoutOptions = {
    '1x1': { rows: 1, cols: 1 },
    '1x2': { rows: 1, cols: 2 },
    '1x3': { rows: 1, cols: 3 },
    '2x1': { rows: 2, cols: 1 },
    '2x2': { rows: 2, cols: 2 },
    '2x3': { rows: 2, cols: 3 },
    '3x1': { rows: 3, cols: 1 },
    '3x2': { rows: 3, cols: 2 },
    '3x3': { rows: 3, cols: 3 },
};

const Dashboard: React.FC = () => {
    const authorization = useSelector((state: RootState) => state.persisted?.authorizationInfo);
    const user = useSelector((state: RootState) => state.auth?.user);
    const [layout, setLayout] = useState<WidgetPlacement[]>([]);
    const [layoutKey, setLayoutKey] = useState<string>('3x3');
    const [widgets, setWidgets] = useState<Widget[]>([]);

    const [fetchLayout] = useLazyGetApiDashboardLayoutQuery();
    const [fetchWidgets] = useLazyGetApiDashboardWidgetsQuery();

    useEffect(() => {
        if (authorization?.roleIds?.length === 0 || !user) return;

        void (async () => {
            const layoutRes = await fetchLayout({
                companyId: user.companyId ?? "",
                role: GetHighestAuthOrder(authorization?.roleIds ?? [])
            }).unwrap();

            const widgetRes = await fetchWidgets().unwrap();

            if (layoutRes) {
                const typedLayout = layoutRes as LayoutResponse;
                setLayout(typedLayout.grid ?? []);
                setLayoutKey(typedLayout.layoutKey ?? '3x3');
            }

            setWidgets(widgetRes as any);
        })();
    }, [authorization, user]);

    const getWidgetAt = (row: number, col: number) => {
        return layout.find(w => w.row === row && w.col === col);
    };

    const config = layoutOptions[layoutKey] ?? layoutOptions['3x3'];

    return (
        <div
            className="w-full h-full gap-0"
            style={{
                display: 'grid',
                gridTemplateRows: `repeat(${config.rows}, 1fr)`,
                gridTemplateColumns: `repeat(${config.cols}, 1fr)`
            }}
        >
            {[...Array(config.rows)].map((_, row) => (
                [...Array(config.cols)].map((_, col) => {
                    const cell = getWidgetAt(row, col);
                    const widget = widgets.find(w => w.id === cell?.widgetId);
                    return (
                        <div key={`cell-${row}-${col}`} className="flex items-stretch justify-stretch m-[5px]">
                            <div className="w-full h-full m-[5px]">
                                {cell && widget ? (
                                    <WidgetRenderer
                                        componentKey={widget.componentKey}
                                        rowSpan={config.rows}
                                        colSpan={config.cols}
                                    />
                                ) : null}
                            </div>
                        </div>
                    );
                })
            ))}
        </div>
    );
};

export default Dashboard;